import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
  addAdminTicketMessage,
  addTicketMessage,
  getAdminTicket,
  getTicket,
  type SupportTicketDetail,
  type SupportTicketMessage,
} from '../../../services/helpTicketService';
import { formatDateTime } from '../shared/dateFormat';
import { ApiError } from '../../../lib/apiClient';
import { parseTicketDetails } from './ticketContactInfo';
import PrimaryButton from '../shared/PrimaryButton';
import { tintFromAccent } from '../steps/_shared';

interface TicketThreadPanelProps {
  ticketId: string;
  admin?: boolean;
  accentColor?: string;
  primaryColor?: string;
  onStatusChange?: () => void;
  onTicketViewed?: (ticketId: string) => void;
}

const TicketThreadPanel: React.FC<TicketThreadPanelProps> = ({
  ticketId,
  admin = false,
  accentColor,
  primaryColor,
  onTicketViewed,
}) => {
  const [ticket, setTicket] = useState<SupportTicketDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [replyOpen, setReplyOpen] = useState(false);
  const replyInputRef = useRef<HTMLTextAreaElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = admin ? await getAdminTicket(ticketId) : await getTicket(ticketId);
      setTicket(data);
      if (admin) onTicketViewed?.(ticketId);
    } catch (e: unknown) {
      setError(e instanceof ApiError ? e.message : 'Failed to load ticket.');
    } finally {
      setLoading(false);
    }
  }, [ticketId, admin, onTicketViewed]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    setReplyOpen(false);
    setReply('');
  }, [ticketId]);

  useEffect(() => {
    if (replyOpen) {
      replyInputRef.current?.focus();
    }
  }, [replyOpen]);

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    const text = reply.trim();
    if (!text || sending) return;
    setSending(true);
    try {
      if (admin) await addAdminTicketMessage(ticketId, text);
      else await addTicketMessage(ticketId, text);
      setReply('');
      setReplyOpen(false);
      await load();
    } catch (e: unknown) {
      setError(e instanceof ApiError ? e.message : 'Failed to send message.');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8 text-slate-500">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
      </div>
    );
  }

  if (error && !ticket) {
    return <p className="text-sm text-red-600 py-4">{error}</p>;
  }

  if (!ticket) return null;

  const { campaignName, issueTitle, issueDetails } = parseTicketDetails(ticket.details);

  const messageBody = (m: SupportTicketMessage, index: number) => {
    if (index === 0 && m.senderRole === 'advertiser' && (campaignName || issueTitle)) {
      return issueDetails;
    }
    return m.body;
  };

  const messages = ticket.messages ?? [];
  const problemDescription =
    messages.length > 0 && messages[0].senderRole === 'advertiser'
      ? messageBody(messages[0], 0)
      : issueDetails || '—';
  const followUpMessages = messages.length > 1 ? messages.slice(1) : [];

  const adminBubbleStyle =
    accentColor && primaryColor && !admin
      ? {
          background: `linear-gradient(180deg, ${accentColor}, ${primaryColor})`,
          color: '#fff',
        }
      : undefined;

  const replyForm = ticket.status !== 'completed' && (
    <form onSubmit={handleSend} className="space-y-2">
      <textarea
        ref={replyInputRef}
        rows={3}
        value={reply}
        onChange={(e) => setReply(e.target.value)}
        placeholder="Write your reply…"
        className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-[color:var(--accent,#94a3b8)] focus:border-[color:var(--accent,#94a3b8)]"
      />
      {error && <p className="text-xs text-red-600">{error}</p>}
      <div className="flex flex-wrap items-center gap-2">
        {admin || !accentColor ? (
          <button
            type="submit"
            disabled={sending || !reply.trim()}
            className="rounded-lg bg-slate-900 text-white px-4 py-2 text-sm font-medium hover:bg-slate-800 disabled:opacity-50 inline-flex items-center gap-2"
          >
            {sending && <Loader2 className="w-4 h-4 animate-spin" />}
            Send reply
          </button>
        ) : (
          <PrimaryButton
            type="submit"
            disabled={sending || !reply.trim()}
            accentColor={accentColor}
            primaryColor={primaryColor}
            size="sm"
          >
            {sending && <Loader2 className="w-4 h-4 animate-spin" />}
            Send reply
          </PrimaryButton>
        )}
        {admin && (
          <button
            type="button"
            onClick={() => {
              setReplyOpen(false);
              setReply('');
              setError(null);
            }}
            className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
          >
            Cancel
          </button>
        )}
      </div>
    </form>
  );

  if (admin) {
    return (
      <div className="space-y-4">
        <div className="rounded-xl border border-slate-200 overflow-hidden shadow-sm flex flex-col">
          <div className="bg-slate-800 text-white px-4 py-2.5 text-sm font-semibold tracking-tight">
            Problem Description
          </div>
          <div className="bg-slate-50 flex flex-col min-h-[6rem]">
            <div className="px-4 py-3 text-sm text-slate-800 whitespace-pre-wrap leading-relaxed">
              {problemDescription}
            </div>
            {ticket.status !== 'completed' && !replyOpen && (
              <div className="flex-1 flex items-center px-4 py-2 border-t border-slate-200/80">
                <button
                  type="button"
                  onClick={() => setReplyOpen(true)}
                  className="rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-100 hover:border-slate-400 transition-colors"
                >
                  Reply
                </button>
              </div>
            )}
          </div>
        </div>

        {followUpMessages.length > 0 && (
          <div className="space-y-3 max-h-[280px] overflow-y-auto pr-1">
            {followUpMessages.map((m: SupportTicketMessage) => (
              <div
                key={m.id}
                className={`rounded-xl px-3 py-2.5 text-sm ${
                  m.senderRole === 'advertiser'
                    ? 'bg-white border border-slate-200 ml-0 mr-10'
                    : m.senderRole === 'system'
                      ? 'bg-amber-50 border border-amber-100 text-amber-900 text-center mx-4'
                      : 'bg-slate-800 text-white ml-10 mr-0'
                }`}
              >
                <div className="whitespace-pre-wrap">{m.body}</div>
                <div
                  className={`text-[10px] mt-1.5 ${
                    m.senderRole === 'admin' ? 'text-slate-300' : 'text-slate-400'
                  }`}
                >
                  {formatDateTime(m.createdAt)}
                </div>
              </div>
            ))}
          </div>
        )}

        {replyOpen && replyForm}

        {ticket.status === 'completed' && followUpMessages.length === 0 && (
          <p className="text-xs text-slate-500">This ticket is completed.</p>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {!admin && (
      <div
        className="rounded-lg border px-3 py-2 text-sm text-slate-700 space-y-2"
        style={
          accentColor
            ? { borderColor: tintFromAccent(accentColor, 0.2), backgroundColor: tintFromAccent(accentColor, 0.04) }
            : { borderColor: '#e2e8f0', backgroundColor: '#f8fafc' }
        }
      >
        <div>
          <div className="font-medium text-slate-900">
            {issueTitle ?? ticket.categoryLabel}
          </div>
          {issueTitle && (
            <div className="text-xs text-slate-500 mt-0.5">{ticket.categoryLabel}</div>
          )}
          <div className="text-xs text-slate-500 mt-1">
            {ticket.statusLabel} · {formatDateTime(ticket.createdAt)}
          </div>
        </div>
        <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs border-t border-slate-200 pt-2">
          {ticket.contactName && (
            <>
              <dt className="text-slate-500">Name</dt>
              <dd className="text-slate-800">{ticket.contactName}</dd>
            </>
          )}
          {campaignName && (
            <>
              <dt className="text-slate-500">Campaign</dt>
              <dd className="text-slate-800">{campaignName}</dd>
            </>
          )}
          <dt className="text-slate-500">Email</dt>
          <dd className="text-slate-800">{ticket.email}</dd>
        </dl>
      </div>
      )}

      <div className="space-y-3 max-h-[320px] overflow-y-auto pr-1">
        {messages.map((m: SupportTicketMessage, index: number) => (
          <div
            key={m.id}
            className={`rounded-lg px-3 py-2 text-sm ${
              m.senderRole === 'advertiser'
                ? 'bg-white border border-slate-200 ml-0 mr-8'
                : m.senderRole === 'system'
                  ? 'bg-amber-50 border border-amber-100 text-amber-900 text-center mx-4'
                  : m.senderRole === 'admin'
                    ? `${adminBubbleStyle ? '' : 'bg-slate-900 '}text-white ml-8 mr-0`
                    : ''
            }`}
            style={m.senderRole === 'admin' && !admin ? adminBubbleStyle : undefined}
          >
            <div className="whitespace-pre-wrap">{messageBody(m, index)}</div>
            <div
              className={`text-[10px] mt-1 ${
                m.senderRole === 'admin' ? 'text-slate-300' : 'text-slate-400'
              }`}
            >
              {formatDateTime(m.createdAt)}
            </div>
          </div>
        ))}
      </div>

      {ticket.status !== 'completed' && replyForm}
    </div>
  );
};

export default TicketThreadPanel;
